import { JSX, useMemo } from "react";

import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";

type Stage = {
  name: string;
  description?: string;
  order: number;
  count: number;
  referral_percentage: number;
  conversion_rate: number | null;
  drop_count: number;
  drop_percentage: number;
  cum_tat_days?: number | null;
};

type Summary = {
  total_referrals: number;
  therapy_covered: number;
  success_rate: number;
  biggest_drop?: {
    from_stage: string;
    to_stage: string;
    lost: number;
    conversion_rate: number;
    drop_percentage: number;
  };
};

type CustomBarData = {
  total_count?: number;
  label?: string;
  summary?: Summary;
  stages?: Stage[];
};

const STAGE_COLORS = [
  "#1B2D4F",
  "#3D8B8B",
  "#7044A0",
  "#C08B3E",
  "#3A7FC2",
  "#2A7556",
  "#8B4570",
  "#5A6B8A",
];

const STAGE_GROUPS: { indices: number[]; label?: string }[] = [
  { indices: [0, 1] },
  { indices: [2, 3, 4], label: "← BV Cycle" },
  { indices: [5], label: "← PA Stages" },
];

type CustomBarWidgetProps = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

const unwrap = (raw: unknown): CustomBarData => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.stages)) return obj as unknown as CustomBarData;
  if (
    obj.data &&
    typeof obj.data === "object" &&
    Array.isArray((obj.data as Record<string, unknown>).stages)
  )
    return obj.data as CustomBarData;
  return obj as unknown as CustomBarData;
};

// has_download_options/download_options sit at the top level of the raw
// response, siblings of whatever `stages` is nested under (same envelope
// shape as InfoBarWidget/DonutPieWidget's own unwrap) — read directly from
// `raw` rather than from `unwrap()`'s result, which only returns the
// stages-bearing object and may or may not be `raw` itself.
type CustomBarEnvelope = {
  has_download_options?: boolean;
  download_options?: WidgetDownloadOptions;
};

const unwrapEnvelope = (raw: unknown): CustomBarEnvelope => {
  if (!raw || typeof raw !== "object") return {};
  return raw as CustomBarEnvelope;
};

export default function CustomBarWidget({
  isLoading,
  data,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: CustomBarWidgetProps): JSX.Element {
  const parsed = useMemo(() => unwrap(data), [data]);
  const envelope = useMemo(() => unwrapEnvelope(data), [data]);
  const stages = useMemo(
    () => [...(parsed.stages ?? [])].sort((a, b) => a.order - b.order),
    [parsed.stages],
  );

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={
        envelope.has_download_options ? envelope.download_options : undefined
      }
    />
  );

  const maxCount = stages.length > 0 ? stages[0].count : 1;
  const totalReferrals = parsed.summary?.total_referrals ?? maxCount;

  const hasTat = stages.some(
    (s) => s.cum_tat_days !== undefined && s.cum_tat_days !== null,
  );

  if (isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <>
        {downloadMenu}
        <p className="text-muted small mb-0">No stage data available.</p>
      </>
    );
  }

  const renderRow = (stage: Stage, idx: number) => {
    const widthPct = (stage.count / maxCount) * 100;
    const color = STAGE_COLORS[idx % STAGE_COLORS.length];
    const isFirst = idx === 0;
    const dropCount = stage.drop_count;
    const dropPct = stage.drop_percentage;
    const isPositiveDrop = dropCount < 0;

    return (
      <div key={stage.name} className="custom-bar-funnel__row">
        <div className="custom-bar-funnel__label" title={stage.name}>
          {stage.name}
        </div>
        <div className="custom-bar-funnel__bar-area">
          <div
            className="custom-bar-funnel__bar"
            style={{
              width: `${Math.max(widthPct, 4)}%`,
              backgroundColor: color,
            }}
          >
            <span className="custom-bar-funnel__bar-value">
              {stage.count.toLocaleString()}
              {!isFirst && (
                <span className="custom-bar-funnel__bar-pct">
                  {" "}
                  · {stage.referral_percentage.toFixed(1)}%
                </span>
              )}
            </span>
          </div>
        </div>

        {hasTat && (
          <div className="custom-bar-funnel__tat">
            {stage.cum_tat_days != null ? (
              <span className="custom-bar-funnel__tat-value">
                {stage.cum_tat_days} d
              </span>
            ) : (
              <span className="custom-bar-funnel__drop-dash">—</span>
            )}
          </div>
        )}

        <div className="custom-bar-funnel__drop">
          {isFirst ? (
            <span className="custom-bar-funnel__drop-dash">—</span>
          ) : dropCount === 0 && dropPct <= 0 ? (
            <span className="custom-bar-funnel__drop-dash">—</span>
          ) : (
            <span
              className={`custom-bar-funnel__drop-value ${
                isPositiveDrop
                  ? "custom-bar-funnel__drop-value--gain"
                  : "custom-bar-funnel__drop-value--loss"
              }`}
            >
              <span className="custom-bar-funnel__drop-count">
                {dropCount > 0
                  ? `-${dropCount.toLocaleString()}`
                  : `+${Math.abs(dropCount).toLocaleString()}`}
              </span>
              <span className="custom-bar-funnel__drop-pct">
                ({Math.abs(dropPct).toFixed(1)}%)
              </span>
            </span>
          )}
        </div>
      </div>
    );
  };

  const groups = STAGE_GROUPS.filter((g) =>
    g.indices.some((i) => i < stages.length),
  );
  const allGroupedIndices = STAGE_GROUPS.flatMap((g) => g.indices);

  return (
    <div className="custom-bar-funnel">
      {downloadMenu}
      <div className="custom-bar-funnel__header">
        <span className="custom-bar-funnel__total">
          Total Referrals: {totalReferrals.toLocaleString()}
        </span>
        <div className="custom-bar-funnel__header-right">
          {hasTat && <span className="custom-bar-funnel__tat-label">TAT</span>}
          <span className="custom-bar-funnel__drop-label">DROP</span>
        </div>
      </div>

      <div className="custom-bar-funnel__stages">
        {groups.map((group, gi) => (
          <div key={gi}>
            {group.label && (
              <div className="custom-bar-funnel__group-label">
                {group.label}
              </div>
            )}
            <div className="custom-bar-funnel__group">
              {group.indices
                .filter((i) => i < stages.length)
                .map((i) => renderRow(stages[i], i))}
            </div>
          </div>
        ))}
        {stages.length > allGroupedIndices.length && (
          <div>
            <div className="custom-bar-funnel__group-label">
              ← Ref ← Covered
            </div>
            <div className="custom-bar-funnel__group">
              {stages
                .slice(allGroupedIndices.length)
                .map((stage, i) =>
                  renderRow(stage, i + allGroupedIndices.length),
                )}
            </div>
          </div>
        )}
        {stages.length <= allGroupedIndices.length && (
          <div className="custom-bar-funnel__group-label">← Ref ← Covered</div>
        )}
      </div>

      {parsed.summary && (
        <div className="custom-bar-funnel__summary">
          <div className="custom-bar-funnel__summary-item">
            <span className="custom-bar-funnel__summary-label">
              Success Rate
            </span>
            <span className="custom-bar-funnel__summary-value custom-bar-funnel__summary-value--success">
              {parsed.summary.success_rate.toFixed(1)}%
            </span>
          </div>
          {parsed.summary.biggest_drop && (
            <div className="custom-bar-funnel__summary-item">
              <span className="custom-bar-funnel__summary-label">
                Biggest Drop
              </span>
              <span className="custom-bar-funnel__summary-value custom-bar-funnel__summary-value--danger">
                {parsed.summary.biggest_drop.from_stage} →{" "}
                {parsed.summary.biggest_drop.to_stage} (
                {parsed.summary.biggest_drop.lost.toLocaleString()} lost)
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
